import React, { useState, useEffect, useRef } from 'react';
import { Bell as BellIcon } from 'lucide-react';
import { notificationApi } from '../services/kataBoardV2';
import type { 
  Notification, 
  NOTIFICATION_TYPE_DISPLAY, 
  NOTIFICATION_PRIORITY_COLORS, 
  NOTIFICATION_TYPE_ICONS 
} from '../types/kataBoardV2';

interface NotificationCenterProps {
  className?: string;
}

const NotificationCenter: React.FC<NotificationCenterProps> = ({ className = '' }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Load notifications (optimized to single API call)
  const loadNotifications = async () => {
    setLoading(true);
    try {
      const { notifications } = await notificationApi.getAll({ 
        limit: showAll ? 50 : 10,
        is_read: showAll ? undefined : false 
      });
      
      setNotifications(notifications);
      // No longer tracking unread_count to eliminate polling
    } catch (error) {
      console.error('Failed to load notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load notifications only when showAll changes (not on mount)
  useEffect(() => {
    if (isOpen) {
      loadNotifications();
    }
  }, [showAll, isOpen]);

  // NO MORE POLLING! Load notifications only when user clicks the bell icon
  // This eliminates unnecessary API calls and improves performance dramatically

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Mark notification as read
  const markAsRead = async (notification: Notification) => {
    if (notification.is_read) return;

    try {
      await notificationApi.markAsRead(notification.id);
      setNotifications(prev => 
        prev.map(n => 
          n.id === notification.id 
            ? { ...n, is_read: true, read_at: new Date().toISOString() }
            : n
        )
      );
      // No need to update unreadCount since we removed the badge
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      const result = await notificationApi.markAllAsRead();
      if (result.count > 0) {
        setNotifications(prev => 
          prev.map(n => ({ ...n, is_read: true, read_at: new Date().toISOString() }))
        );
        // No need to update unreadCount since we removed the badge
      }
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  };

  // Handle notification click
  const handleNotificationClick = (notification: Notification) => {
    markAsRead(notification);
    
    // Navigate to related page if action URL is available
    if (notification.metadata?.action_url) {
      window.location.href = notification.metadata.action_url;
    }
  };

  // Get notification type display
  const getTypeDisplay = (type: string): string => {
    const typeDisplayMap: Record<string, string> = {
      pdca_created: 'PDCA Cycle Created',
      pdca_assigned: 'PDCA Cycle Assigned',
      pdca_due_soon: 'PDCA Cycle Due Soon',
      pdca_overdue: 'PDCA Cycle Overdue',
      pdca_completed: 'PDCA Cycle Completed',
      pdca_updated: 'PDCA Cycle Updated',
      observation_created: 'Process Observation Created',
      observation_updated: 'Process Observation Updated'
    };
    return typeDisplayMap[type] || type;
  };

  // Get notification icon
  const getTypeIcon = (type: string): string => {
    const typeIconMap: Record<string, string> = {
      pdca_created: '📋',
      pdca_assigned: '👤',
      pdca_due_soon: '⏰',
      pdca_overdue: '🚨',
      pdca_completed: '✅',
      pdca_updated: '📝',
      observation_created: '🔍',
      observation_updated: '📊'
    };
    return typeIconMap[type] || '📢';
  };

  // Get priority colors
  const getPriorityColors = (priority: string): string => {
    const priorityColorMap: Record<string, string> = {
      low: 'text-green-600 bg-green-100',
      medium: 'text-blue-600 bg-blue-100',
      high: 'text-yellow-600 bg-yellow-100',
      urgent: 'text-red-600 bg-red-100'
    };
    return priorityColorMap[priority] || 'text-gray-600 bg-gray-100';
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Notification Bell Button */}
      <button
        onClick={() => {
          const newIsOpen = !isOpen;
          setIsOpen(newIsOpen);
          // Load notifications only when opening the dropdown
          if (newIsOpen) {
            loadNotifications();
          }
        }}
        className="relative p-2 text-white hover:text-gray-200 hover:bg-white/10 rounded-lg transition-colors"
        aria-label="Notifications"
      >
        <BellIcon className="w-6 h-6" />
        
        {/* Unread Badge removed - eliminates need for constant polling */}
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Notifications</h3>
            <div className="flex items-center space-x-2">
              <button
                onClick={markAllAsRead}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                Mark all read
              </button>
              <button
                onClick={() => setShowAll(!showAll)}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                {showAll ? 'Show unread' : 'Show all'}
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="p-4 text-center text-gray-500">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto mb-2"></div>
                Loading notifications...
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
                </svg>
                <p className="text-sm">No notifications yet</p>
                <p className="text-xs text-gray-400 mt-1">
                  You'll see PDCA cycle updates and other notifications here
                </p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors ${
                      !notification.is_read ? 'bg-blue-50' : ''
                    }`}
                  >
                    <div className="flex items-start space-x-3">
                      {/* Icon */}
                      <div className="flex-shrink-0">
                        <span className="text-2xl">
                          {getTypeIcon(notification.notification_type)}
                        </span>
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        {/* Header */}
                        <div className="flex items-center justify-between mb-1">
                          <p className={`text-sm font-medium ${
                            !notification.is_read ? 'text-gray-900' : 'text-gray-700'
                          }`}>
                            {notification.title}
                          </p>
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            getPriorityColors(notification.priority)
                          }`}>
                            {notification.priority}
                          </span>
                        </div>

                        {/* Message */}
                        <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                          {notification.message}
                        </p>

                        {/* Footer */}
                        <div className="flex items-center justify-between text-xs text-gray-500">
                          <span>{getTypeDisplay(notification.notification_type)}</span>
                          <span>{notification.time_ago}</span>
                        </div>

                        {/* Related Info */}
                        {notification.pdca_cycle_title && (
                          <div className="mt-2 text-xs text-blue-600 bg-blue-50 rounded px-2 py-1">
                            📋 {notification.pdca_cycle_title}
                          </div>
                        )}
                      </div>

                      {/* Unread Indicator */}
                      {!notification.is_read && (
                        <div className="flex-shrink-0">
                          <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="px-4 py-3 border-t border-gray-200 text-center">
              <button
                onClick={() => {
                  // Navigate to full notifications page
                  window.location.href = '/notifications';
                }}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                View all notifications
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationCenter;






